import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { ActionToEventMapping, createFrom, EventService } from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { ActiveCartFacade } from '../facades';
import { CartEntryActionTypes } from '../store/actions/cart-entry.action';
import {
  AddA4SampleCartEntryEvent,
  AddA4SampleCartEntryFailEvent,
  AddA4SampleCartEntrySuccessEvent,
  AddCartEntriesEvent,
  AddCartEntriesFailEvent,
  AddCartEntriesSuccessEvent,
  AddCartEntryEvent,
  AddCartEntryFailEvent,
  AddCartEntrySuccessEvent,
  RemoveCartEntriesFailEvent,
  RemoveCartEntriesSuccessEvent,
  RemoveCartEntryFailEvent,
  RemoveCartEntrySuccessEvent,
  UpdateCartEntryFailEvent,
  UpdateCartEntrySuccessEvent,
} from './cart.events';

@Injectable({
  providedIn: 'root',
})
export class CartEventBuilder {
  constructor(
    private actionsSubject: ActionsSubject,
    private eventService: EventService,
    private activeCartService: ActiveCartFacade
  ) {
    this.register();
  }

  private register(): void {
    this.registerAddCartEntry();
    this.registerAddA4SampleCartEntry();
    this.registerQuickAddCartEntry();
    this.registerAddCartEntries();
    this.registerRemoveCartEntry();
    this.registerRemoveCartEntries();
    this.registerUpdateCartEntry();
  }

  private registerAddCartEntry(): void {
    this.registerMapped({
      action: CartEntryActionTypes.AddCartEntry,
      event: AddCartEntryEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.AddCartEntrySuccess,
      event: AddCartEntrySuccessEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.AddCartEntryFail,
      event: AddCartEntryFailEvent,
    });
  }

  private registerAddA4SampleCartEntry(): void {
    this.registerMapped({
      action: CartEntryActionTypes.AddA4SampleCartEntry,
      event: AddA4SampleCartEntryEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.AddA4SampleCartEntrySuccess,
      event: AddA4SampleCartEntrySuccessEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.AddA4SampleCartEntryFail,
      event: AddA4SampleCartEntryFailEvent,
    });
  }

  private registerQuickAddCartEntry(): void {
    this.registerMapped({
      action: CartEntryActionTypes.QuickAddCartEntry,
      event: AddCartEntryEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.QuickAddCartEntrySuccess,
      event: AddCartEntrySuccessEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.QuickAddCartEntryFail,
      event: AddCartEntryFailEvent,
    });
  }

  private registerAddCartEntries(): void {
    this.registerMapped({
      action: CartEntryActionTypes.AddCartEntries,
      event: AddCartEntriesEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.AddCartEntriesSuccess,
      event: AddCartEntriesSuccessEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.AddCartEntriesFail,
      event: AddCartEntriesFailEvent,
    });
  }

  private registerRemoveCartEntry(): void {
    this.registerMapped({
      action: CartEntryActionTypes.RemoveCartEntrySuccess,
      event: RemoveCartEntrySuccessEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.RemoveCartEntryFail,
      event: RemoveCartEntryFailEvent,
    });
  }

  private registerRemoveCartEntries(): void {
    this.registerMapped({
      action: CartEntryActionTypes.RemoveAllCartEntriesSuccess,
      event: RemoveCartEntriesSuccessEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.RemoveAllCartEntriesFail,
      event: RemoveCartEntriesFailEvent,
    });
  }

  private registerUpdateCartEntry(): void {
    this.registerMapped({
      action: CartEntryActionTypes.UpdateCartEntrySuccess,
      event: UpdateCartEntrySuccessEvent,
    });
    this.registerMapped({
      action: CartEntryActionTypes.UpdateCartEntryFail,
      event: UpdateCartEntryFailEvent,
    });
  }

  /**
   * Registers a stream of target events mapped from the source actions that contain the cart id equal to the active cart id.
   *
   * @param mapping mapping declaration - from `action` string type to `event` class type
   *   (an with optional `factory` function - by default `action.payload` will be assigned to the properties of the event instance).
   */
  private registerMapped<T>(mapping: ActionToEventMapping<T>): () => void {
    const eventStream$ = this.getAction(mapping.action).pipe(
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.activeCartService.getActiveCart(action.payload['cartType']).pipe(
            filter((cart) => Boolean(cart)),
            take(1)
          ),
        ])
      ),
      filter(([action, activeCart]) => action.payload['cartId'] === activeCart.code),
      map(([action]) =>
        createFrom(mapping.event, {
          ...action.payload,
          entry: action.payload.entry ? action.payload.entry : undefined,
        })
      )
    );
    return this.eventService.register(mapping.event, eventStream$);
  }

  /**
   * Returns a stream of actions only of a given type(s)
   *
   * @param actionType type(s) of actions
   */
  private getAction(actionType: string | string[]): Observable<{ type: string; payload?: any }> {
    return this.actionsSubject.pipe(ofType(...[].concat(actionType)));
  }
}
